/**
 * useEntitlements — snapshot consolidado de entitlements (plano efetivo ⊕ overrides,
 * limites, uso atual, saldos). Lê GET /v1/me/entitlements (Fase 1 do backend).
 *
 * Evolução de useFeatureFlag (que lê /v1/me/subscription e continua válido). As
 * CHAVES de features/limits/usage/meters são snake_case (vêm do banco).
 *
 * `meters` fica vazio até o Billing (Escopo 2) — useMeter já lida com isso.
 */
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';

const ENTITLEMENTS_QUERY_OPTIONS = {
  queryKey: ['entitlements'],
  queryFn: async () => {
    const res = await api.get('/v1/me/entitlements');
    return res.data;
  },
  staleTime: 60_000,
  gcTime: 5 * 60_000,
  refetchOnWindowFocus: false,
};

export function useEntitlements() {
  const { data, isLoading, error, refetch } = useQuery(ENTITLEMENTS_QUERY_OPTIONS);
  return {
    snapshot: data || null,
    planSlug: data?.planSlug || 'free',
    planName: data?.planName || 'Grátis',
    status: data?.status || 'active',
    currentPeriodEnd: data?.currentPeriodEnd || null,
    scheduledPlanSlug: data?.scheduledPlanSlug || null,
    features: data?.features || {},
    limits: data?.limits || {},
    usage: data?.usage || {},
    meters: data?.meters || {},
    warningThresholds: data?.warningThresholds || {},
    isLoading,
    error,
    refetch,
  };
}

/** Feature habilitada? (override ⊕ plano) */
export function useFeature(featureKey) {
  const { features } = useEntitlements();
  return features?.[featureKey] ?? false;
}

/**
 * Limite + uso atual de uma chave.
 * @returns {{ limit:(number|null), current:number, remaining:number, unlimited:boolean, nearLimit:boolean }}
 */
export function useLimit(limitKey) {
  const { limits, usage, warningThresholds } = useEntitlements();
  const hasKey = limits && Object.prototype.hasOwnProperty.call(limits, limitKey);
  const rawLimit = hasKey ? limits[limitKey] : null;
  const unlimited = rawLimit === null || rawLimit === undefined;
  const current = usage?.[limitKey] ?? 0;
  const remaining = unlimited ? Infinity : Math.max(0, rawLimit - current);
  const warn = warningThresholds?.[limitKey];
  const nearLimit = !unlimited && (warn != null ? current >= warn : rawLimit > 0 && current / rawLimit >= 0.8);
  return { limit: unlimited ? null : rawLimit, current, remaining, unlimited, nearLimit };
}

/** Saldo de um consumível (créditos). Vazio até o Billing.
 *  `loading` = snapshot de entitlements ainda carregando — sem isso a UI mostrava saldo 0
 *  antes do valor real chegar (falso "zerado" durante o fetch). */
export function useMeter(meterKey) {
  const { meters, isLoading } = useEntitlements();
  const meter = meters?.[meterKey] ?? { balance: 0, low: false };
  return { ...meter, loading: !!isLoading };
}
